import { useCallback, useRef } from 'react';

/**
 * Fermeture « au clic sur le voile » qui exige un GESTE ENTIER sur le voile.
 *
 * ── LE PIÈGE (il revient à chaque nouvelle surface, d'où ce commentaire) ───────────
 *
 * Un `click` n'est PAS émis sur la cible du relâchement : il est émis sur le PLUS PROCHE
 * ANCÊTRE COMMUN de la cible du `mousedown` et de celle du `mouseup`. Le voile étant
 * l'ancêtre du panneau, DEUX gestes distincts finissent en `click` SUR LE VOILE :
 *
 *   a. appui sur le voile  → relâchement sur le voile   (clic franc, il faut fermer) ;
 *   b. appui DANS le panneau → relâchement sur le voile (sélection de texte au-delà du
 *      bord : il ne faut SURTOUT pas fermer, la saisie serait perdue) ;
 *   c. appui sur le voile  → relâchement DANS le panneau (il ne faut pas fermer non plus).
 *
 * Trois remèdes réflexes échouent :
 *  - `e.target === e.currentTarget` sur le `click` seul : vrai dans les TROIS cas, donc
 *    (b) ferme quand même — c'est le défaut classique ;
 *  - un `stopPropagation` sur le panneau : en (b) le panneau n'est pas sur le chemin de
 *    propagation du `click` (qui part du voile), le gestionnaire n'est jamais atteint ;
 *  - fermer sur le `pointerdown` du voile : (b) est bien guéri, mais (c) ferme désormais
 *    AVANT même la fin du geste — le défaut est déplacé, pas corrigé.
 *
 * ── LA GARDE ──────────────────────────────────────────────────────────────────────
 *
 * On suit le geste complet et on ne ferme que s'il s'est déroulé ENTIÈREMENT sur le voile :
 *  1. `pointerdown` — (RÉ)ARME le drapeau : vrai si le bouton principal appuie sur le voile
 *     LUI-MÊME (`target === currentTarget`), faux pour tout descendant ;
 *  2. `pointerup`   — DÉSARME si le relâchement a lieu sur un descendant (cas c) ;
 *  3. `click`       — ferme si le drapeau tient ET que la cible du clic est le voile, puis
 *     DÉSARME dans tous les cas.
 *
 * L'étape 2 est indispensable : au cas (c), la cible du `click` EST le voile et le drapeau
 * de l'étape 1 est vrai — sans elle la garde laisserait passer.
 *
 * Le réarmement est ce qui empêche de remplacer un défaut par un autre : un drapeau resté
 * armé (geste avorté hors fenêtre, `pointercancel` tactile) fermerait au clic SUIVANT.
 * Il est donc réécrit à chaque `pointerdown`, remis à zéro à chaque `click`, et annulé
 * sur `pointercancel`.
 */
export interface ScrimDismissHandlers {
  onPointerDown: (e: React.PointerEvent<Element>) => void;
  onPointerUp: (e: React.PointerEvent<Element>) => void;
  onPointerCancel: () => void;
  onClick: (e: React.MouseEvent<Element>) => void;
}

/**
 * `onDismiss` à `undefined` (p. ex. pendant un appel réseau) inhibe la fermeture sans rien
 * changer au suivi du geste — le drapeau reste correctement réarmé pour la suite.
 *
 * L'événement `click` lui est transmis pour les fonds qui ajoutent leur PROPRE condition
 * (le fond zoomable de la visionneuse y mesure la distance parcourue, afin qu'un glisser
 * d'un bout à l'autre du fond ne compte pas pour un clic). Les modales l'ignorent.
 */
export function useScrimDismiss(
  onDismiss?: ((e: React.MouseEvent<Element>) => void) | undefined,
): ScrimDismissHandlers {
  const armedRef = useRef(false);

  const onPointerDown = useCallback((e: React.PointerEvent<Element>): void => {
    // Réarmement inconditionnel : jamais de `return` anticipé qui laisserait la valeur
    // du geste PRÉCÉDENT en place. Bouton non principal (clic droit, molette) → désarmé.
    armedRef.current = e.button === 0 && e.target === e.currentTarget;
  }, []);

  const onPointerUp = useCallback((e: React.PointerEvent<Element>): void => {
    if (e.target !== e.currentTarget) armedRef.current = false;
  }, []);

  const onPointerCancel = useCallback((): void => {
    armedRef.current = false;
  }, []);

  const onClick = useCallback(
    (e: React.MouseEvent<Element>): void => {
      const wholeGestureOnScrim = armedRef.current;
      armedRef.current = false;
      if (!wholeGestureOnScrim) return;
      if (e.target !== e.currentTarget) return;
      onDismiss?.(e);
    },
    [onDismiss],
  );

  return { onPointerDown, onPointerUp, onPointerCancel, onClick };
}
